import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query'

import { API_ENDPOINTS } from '../../../common/apiEndpoints'
import {
  ErrorResponse,
  InitConfirmCreateModificationRequest,
  InitConfirmOperationResponse,
  initConfirmOperationResponseSchema,
} from '../../../common/validations'
import { ResidentAppError } from '../../../common/error'
import { getContractQueryKey } from './useAPIGetContract'
import { getContractsQueryKey } from './useAPIGetContracts'

import { post } from 'api/apiClient'
import { rethrowIdentificationRequired } from 'utils/rethrowIdentificationRequired'

export const useAPIConfirmCreateModification = (
  contractUUID: string,
  locale: string,
  options?: UseMutationOptions<
    InitConfirmOperationResponse,
    ErrorResponse,
    Omit<InitConfirmCreateModificationRequest, 'locale'>
  >
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...mutationOptions } = options ?? {}

  const { mutate, isSuccess, isError, error, data, isPending } = useMutation({
    mutationFn: ({ operationData, provider }) =>
      post(
        `${API_ENDPOINTS.CONTRACTS}/${contractUUID}/confirm-operation/init/contract-modification/create`,
        {
          operationData,
          locale,
          provider,
        },
        initConfirmOperationResponseSchema
      ),
    ...mutationOptions,
    onSuccess: async (response, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({
        queryKey: getContractsQueryKey(),
        exact: true,
      })
      await queryClient.invalidateQueries({
        queryKey: getContractQueryKey(contractUUID),
      })

      if (onSuccess) {
        await onSuccess(response, variables, onMutateResult, context)
      }
    },
  })

  if (isError) {
    rethrowIdentificationRequired(error)

    throw ResidentAppError.ClientError('TERMINATE_CONTRACT_INIT_CONFIRM_ERROR')
  }

  return {
    mutate,
    isSuccess,
    data,
    isPending,
  }
}
